import { useState, useEffect, useCallback } from 'react';
import { DASHBOARD_ITEMS_PER_PAGE } from '@/features/dashboard/constants/dashboard.constants';
import { accountService } from '@/features/card/services/accountService/accountService';
import { Transaction } from '@/features/card/types/account.types';
import { useAsyncGuard } from '@chikara/hooks';
import { useSpinner } from '@chikara/ui';
import { isBasic } from '@/shared/config/permissions';
import { t } from '@/i18n';
import { buildGenericFilters, getEffectiveRange } from '@/features/dashboard/utils/dashboardFilters.utils';
import { mapMovementRow } from '@/features/dashboard/utils/dashboardMovements.mapper';
import type { DashboardFilters, MovementCatalogValues } from '@/features/dashboard/types/dashboard.types';

interface UseDashboardMovementsReturn {
  data: Transaction[];
  total: number;
  catalogs: MovementCatalogValues;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDashboardMovements(
  customerUuid: string,
  page: number,
  role?: string,
  userUuid?: string,
  filters?: DashboardFilters
): UseDashboardMovementsReturn {
  const { show, hide } = useSpinner();
  const { from, to, email, cardLast4, status, type } = filters ?? {};
  const [data, setData] = useState<Transaction[]>([]);
  const [total, setTotal] = useState(0);
  const [catalogs, setCatalogs] = useState<MovementCatalogValues>({ statuses: [], types: [] });
  const [error, setError] = useState<string | null>(null);

  const { checkGuard, resetGuard } = useAsyncGuard();

  const fetchMovements = useCallback(async () => {
    if (!customerUuid || !userUuid || !checkGuard()) return;
    show();
    setError(null);
    try {
      const effectiveRange = getEffectiveRange({ from, to });
      const genericFilters = buildGenericFilters({
        email,
        cardLast4,
        status,
        type,
        from: effectiveRange.from,
        to: effectiveRange.to,
      });
      const txs = await accountService.getAccountTransactions({
        ...(isBasic(role) && userUuid ? {
          user: {
            uuid: userUuid
          }
        } : {
          customer: {
            uuid: customerUuid
          }
        }),
        filter: {
          page_number: page,
          results_per_page: DASHBOARD_ITEMS_PER_PAGE,
          genericFilters
        }
      });

      const tx = txs?.result?.tx?.map(mapMovementRow);
      setData(tx);
      setTotal(txs.result.total_documents);
      setCatalogs(txs.result.catalogs ?? { statuses: [], types: [] });
    } catch {
      setError(t('dashboard.errors.generic'));
      resetGuard();
    } finally {
      hide();
    }
  }, [customerUuid, userUuid, role, page, from, to, email, cardLast4, status, type, show, hide, checkGuard, resetGuard]);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  return {
    data,
    total,
    catalogs,
    error,
    refetch: () => {
      resetGuard();
      return fetchMovements();
    }
  };
}
